import React, { MutableRefObject } from "react"
import { match, P } from "ts-pattern"
import { Trans } from "@lingui/macro"
import { ECommerceState } from "../ecommerce-machine/e-commerce-machine"
import {
    canGoToCategoriesV4,
    canGoToFareV4,
    canGoToSendQuoteV4,
    canSelectSuiteCategoryV4,
} from "../ecommerce-machine/e-commerce-machine-transitions"
import { homeV4, isRoutePathV4, isRouteV4, RoutePathV4, routeV4 } from "../../../../_common/routing"
import VsMemberCheckbox from "../../../fare/VsMemberCheckbox"
import { WebAppConfig } from "../../../../_common/web-app-config"
import SuitesComparisonBox from "../../../category/SuitesComparisonBox"
import { MachinesContextType } from "../../../../_common/machines/machines-context"
import { DynamicFAQVisibleInStep } from "../../../../_common/sanity/sanity-types"
import HeaderFAQButton from "../../../../../components/Header/HeaderFAQButton"
import FloatingFAQLink from "../../../../../components/FloatingFAQLink/FloatingFAQLink"
import DynamicFAQSection from "../../../../../components/DynamicFAQSection/DynamicFAQSection"
import { dataLoadingConfigV4, dataLoadingOptionsV4 } from "../ecommerce-machine/data-loading-state/v4"
import { BookingSteps } from "../../../../_common/steps/booking-steps"
import StepsRecapPriceConfigV4 from "../../../../../components/Sidebar/StepsRecap/StepsRecapPriceConfigV4"
import { useUrlSyncV4 } from "../../hooks/use-url-sync-v4"
import { FarePriceLabelV4 } from "../../../fare/components/Infographic/FarePriceLabelV4"
import { SuiteCardPriceV4 } from "../../../../../components/CruiseSuiteCard/SuiteCardPriceV4"
import { SuiteDetailsPriceV4 } from "../../../../../components/SuiteModal/SuiteDetailsPriceV4"
import { defaultSendUserEventV4, userEventsMachineV4 } from "../user-events-machine-v4"
import { visiblePriceConfigStepsV4 } from "./steps/PriceConfigStepsNavV4"
import { FlowVersion } from "./flow-version"
import { StandardSuiteModalButton } from "./StandardSuiteModalButton"
import { StandardCardButton } from "./StandardCardButton"
import { StandardSuitesComparisonButton } from "./StandardSuitesComparisonButton"
import { GuaranteedCardImageFooter } from "./CardImageFooter"
import { isTrackingPageV4, trackingStepV4 } from "./tracking-step-v4"
import { receiptMachineV4 } from "./receipt-machine-v4"
import { raiseEventsForCheckoutRestoredV4 } from "./flow-actions-v4"

export const PriceConfigV4Flow = (
    _: WebAppConfig,
    machinesContextRef: MutableRefObject<MachinesContextType | undefined>,
): FlowVersion => {
    return {
        // TODO - unbundling: review partial feature for "Get Summary" on suite cards https://silversea-ssc.atlassian.net/browse/DP-1478
        availabilities: ["standard", "guaranteed"],
        tracking: {
            //NOTE: keep in sync with the one we use on the backend (tracking schema version)
            version: 4,
            pageCat1: "price-configurator",
            step: (activeStep, subStep) => {
                if (!isTrackingPageV4(activeStep))
                    throw new Error(`activeStep is not a TrackingPageV4, but ${activeStep}`)
                if (subStep && !isTrackingPageV4(subStep))
                    throw new Error(`subStep is not a TrackingPageV4, but ${subStep}`)
                return trackingStepV4(activeStep, subStep)
            },
            subSteps: {
                webQuoteThankYou: "web-quote-thank-youV4",
                webQuoteForm: "web-quote-formV4",
                courtesyHoldThankYou: "courtesy-hold-thank-youV4",
                webQuoteExitIntent: "web-quote-exit-intentV4",
            },
        },
        steps: {
            initialActiveStep: "guestsV4",
            steps: visiblePriceConfigStepsV4,
            canGoToNextStep,
        },
        router: {
            routeNextStep: (currentStep) => routeV4(goToNextStepPath(currentStep)),
            useUrlSync: useUrlSyncV4,
            homeUrl: (cruiseCode) => homeV4(cruiseCode),
            route: (route) => {
                if (!isRoutePathV4(route)) throw new Error(`route ${route} is not a valid RoutePathV4`)
                return routeV4(route)
            },
            isRoute: (router, route) => {
                if (!isRoutePathV4(route)) throw new Error(`isRoute: ${route} is not a valid RoutePathV4`)
                return isRouteV4(router, route)
            },
        },
        ui: {
            vsMemberCheckbox: () => <VsMemberCheckbox />,
            courtesyHoldBoxSidebar: () => null,
            courtesyHoldBottomBar: () => null,
            suiteCategoryNotReadyToBookBanner: () => null,
            suitesComparison: (state) => (showSuiteComparison(state) ? <SuitesComparisonBox /> : null),
            automaticSelectionOfSuiteNumberInProgress: () => false,
            suiteCategory: {
                CardButton: StandardCardButton(machinesContextRef),
                CardImageFooter: ({ availability }) =>
                    match(availability)
                        .with(P.union("standard", "partial"), () => null)
                        .with("guaranteed", () => <GuaranteedCardImageFooter />)
                        .exhaustive(),
                SuiteModalButton: StandardSuiteModalButton(machinesContextRef),
                SuitesComparisonButton: StandardSuitesComparisonButton(machinesContextRef),
                SuiteCardPriceLabelSkeleton: () => <Trans>From</Trans>,
                SuiteCardPrice: SuiteCardPriceV4,
                SuiteDetailsPrice: SuiteDetailsPriceV4,
            },
            stepsRecap: (activeStep) => <StepsRecapPriceConfigV4 activeStep={activeStep} />,
            faqsForStep,
            faq: {
                HeaderButton: (state, props) => (isFAQVisible(state) ? <HeaderFAQButton {...props} /> : null),
                FloatingButton: (state, variant) =>
                    isFAQVisible(state) ? <FloatingFAQLink variant={variant} /> : null,
                Section: (state, content) =>
                    isFAQVisible(state) ? (
                        <div>
                            <DynamicFAQSection content={content} backgroundColor="white" />
                        </div>
                    ) : null,
                isVisible: isFAQVisible,
            },
            showNextButton: (state) =>
                match(state.context.activeStep)
                    .with("send-quoteV4", () => false)
                    .otherwise(() => true),
            useWebQuoteExitIntent: () => ({ openExitIntentModal: nothingToDo, canTrigger: false }),
            canOpenWebQuoteExitIntent: () => false,
            useAutomaticFareSelection: nothingToDo,
            useAutomaticSuiteCategorySelection: nothingToDo,
            useAutomaticStandardSuiteNumberSelection: nothingToDo,
            useAutomaticGuaranteedSuiteNumberSelection: nothingToDo,
            useAutomaticSuiteNumberSelection: nothingToDo,
            pageFlowTitle: () => <Trans>Build your quote by selecting your preferences</Trans>,
            areFareCardsSelectable: canSelectSuiteCategoryV4,
            FarePriceLabel: FarePriceLabelV4,
        },
        stateMachine: {
            dataLoading: {
                state: dataLoadingConfigV4,
                options: dataLoadingOptionsV4,
            },
            nothingSelected: "nothingSelectedV4",
            receipt: receiptMachineV4,
            flow: {
                raiseEventsForCheckoutRestored: raiseEventsForCheckoutRestoredV4,
            },
            userEvents: (context) =>
                userEventsMachineV4(
                    {
                        cruiseCode: context.cruiseCode,
                        sessionId: context.sessionId,
                        guests: context.guests,
                        checkoutStatus: context.checkoutStatus,
                        vsCode: context.vsCode,
                        fareCards: context.fareCards,
                        restoreCompleted: false,
                    },
                    defaultSendUserEventV4(context.config.dotComApiBaseUrl),
                ),
            serverNotification: {
                config: (channel) => ({
                    channel,
                    knownNotifications: {},
                    notifyParent: () => undefined,
                }),
            },
        },
        quoteSource: "PriceConfig",
    }
}

const canGoToNextStep = (currentStep: BookingSteps, state: ECommerceState): boolean =>
    match(currentStep)
        .with("guestsV4", () => canGoToCategoriesV4(state))
        .with("categoryV4", () => canGoToFareV4(state))
        .with("fareV4", () => canGoToSendQuoteV4(state))
        .otherwise(() => false)

const goToNextStepPath = (currentStep: BookingSteps): RoutePathV4 =>
    match<BookingSteps, RoutePathV4>(currentStep)
        .with("guestsV4", () => "category")
        .with("categoryV4", () => "fare")
        .with("fareV4", () => "send-quote")
        .otherwise(() => {
            throw new Error(`Next step not defined (current step: ${currentStep}`)
        })

const showSuiteComparison = (_state: ECommerceState) => false // TODO - suites-comparison: where should we show the suites comparison?

const faqsForStep = (activeStep: BookingSteps) =>
    match<BookingSteps, DynamicFAQVisibleInStep | undefined>(activeStep)
        .with("guestsV4", () => "guests-and-fare")
        .with("categoryV4", () => "suite-category")
        .with("fareV4", () => "guests-and-fare")
        .with("send-quoteV4", () => undefined)
        .otherwise(() => undefined)

const isFAQVisible = (state: ECommerceState) => state.context.activeStep !== "send-quoteV4"

const nothingToDo = () => {}
